import type { WordsFrequencyData } from "@-/dev.improve.haniverse.data.words-frequency";

export const WORD_FREQUENCY_COLUMNS = 8;

export type WordFrequencyRow =
  | { kind: "header"; label: string; subtitle: string; threshold: number }
  | { columns: number; first: boolean; kind: "row"; wordOffset: number; words: readonly string[] }
  | { kind: "footer" };

/**
 * Keep numeric virtualizer IDs and one descriptor per bucket. The bundled word arrays remain the
 * sole catalog: no permanent sliced row arrays, per-row objects, or flattened 85k-word array.
 * Grapheme lookup inputs are generated with the catalog, so construction never scans word text.
 * Construct once for immutable catalog data, never per row or rerender; lookups are O(log buckets).
 */
export function createWordsFrequencyRowIndex(
  data: WordsFrequencyData,
  expandedThresholds?: ReadonlySet<number>,
) {
  let count = 0;
  const buckets = data.buckets.map((bucket) => {
    const start = count;
    const columns = expandedThresholds?.has(bucket.threshold) ? 4 : WORD_FREQUENCY_COLUMNS;
    const wordRows = Math.ceil(bucket.words.length / columns);
    count += wordRows + 2;
    return { bucket, columns, end: count, start };
  });

  function findBucket(id: number) {
    if (!Number.isInteger(id) || id < 0 || id >= count) {
      throw new RangeError(`Invalid word frequency row: ${id}`);
    }
    let lower = 0;
    let upper = buckets.length - 1;
    while (lower < upper) {
      const middle = Math.floor((lower + upper) / 2);
      const bucket = buckets[middle];
      if (bucket === undefined) {
        throw new RangeError(`Missing word frequency bucket: ${middle}`);
      }
      if (id < bucket.end) {
        upper = middle;
      } else {
        lower = middle + 1;
      }
    }
    const bucket = buckets[lower];
    if (bucket === undefined) {
      throw new RangeError(`Missing word frequency bucket: ${lower}`);
    }
    return bucket;
  }

  return {
    getItemType(id: number): WordFrequencyRow["kind"] {
      const { end, start } = findBucket(id);
      return id === start ? "header" : id === end - 1 ? "footer" : "row";
    },
    getKey(id: number): string {
      const { bucket, columns, end, start } = findBucket(id);
      const suffix =
        id === start ? "header" : id === end - 1 ? "footer" : `row-${(id - start - 1) * columns}`;
      return `top-${bucket.threshold}-${suffix}`;
    },
    getRow(id: number): WordFrequencyRow {
      const { bucket, columns, end, start } = findBucket(id);
      if (id === start) {
        return {
          kind: "header",
          label:
            bucket.lowerPct === 0
              ? `Top ${bucket.upperPct}%`
              : `Top ${bucket.lowerPct}%-${bucket.upperPct}%`,
          subtitle: `${bucket.words.length} words`,
          threshold: bucket.threshold,
        };
      }
      if (id === end - 1) {
        return { kind: "footer" };
      }
      return {
        columns,
        first: id === start + 1,
        kind: "row",
        wordOffset: (id - start - 1) * columns,
        words: bucket.words,
      };
    },
    isFirstWordRow(id: number): boolean {
      const { end, start } = findBucket(id);
      return id === start + 1 && id < end - 1;
    },
    rowIds: Array.from({ length: count }, (_, id) => id),
    uniqueGraphemes: data.uniqueGraphemes,
  };
}

const rowIndexByData = new WeakMap<
  WordsFrequencyData,
  ReturnType<typeof createWordsFrequencyRowIndex>
>();

/**
 * {@link createWordsFrequencyRowIndex}, cached per catalog object: the bundled catalog (and each
 * memoized corpus-filtered copy) is indexed once, not on every mount of the view.
 */
export function getWordsFrequencyRowIndex(data: WordsFrequencyData) {
  let rowIndex = rowIndexByData.get(data);
  if (rowIndex === undefined) {
    rowIndex = createWordsFrequencyRowIndex(data);
    rowIndexByData.set(data, rowIndex);
  }
  return rowIndex;
}
